import type { Account, Entry, Vendor } from "@/api/types";
import type { DerivedPaymentAnomaly } from "@/lib/anomaly";
import { apiUrl } from "@/api/client";
import { formatRelativeTime, formatSignedMoney } from "@/lib/format";
import { initialsForVendor, toneForAccount } from "@/lib/category";
import { Icon } from "@/components/Icon";
import { PaymentAnomalyNotice } from "@/components/PaymentAnomalyNotice";
import "./EntryRow.css";

interface EntryRowProps {
  entry: Entry;
  vendor: Vendor | undefined;
  account: Account | undefined;
  isNew: boolean;
  /** See lib/anomaly.ts — a client-derived echo of the payment-anomaly signal. */
  anomaly?: DerivedPaymentAnomaly | null;
}

/**
 * A purchase this size is worth noticing at a glance, so the amount is set
 * heavier. Gives the wall rhythm instead of one flat column of identical
 * figures — a $3,140 input purchase should not look like a $264 vet bill.
 *
 * Presentation only: the number itself is never recomputed here, and this
 * comparison is on a value the server already fixed.
 */
const NOTABLE_AMOUNT = 1000;

/**
 * Q10 (docs/demo/OPEN-QUESTIONS.md): a receipt-sourced entry with no account
 * used to read "Uncategorized · Auto-categorized" — self-contradictory,
 * and visible on exactly the first-scan moment a prospect is watching.
 * "Auto-categorized" is now reserved for rows that actually carry a mapped
 * account; a receipt-sourced row with none reads "Scanned" instead.
 */
function sourceLabel(entry: Entry): string {
  if (entry.source === "Livestock") return "Livestock";
  if (entry.source === "Payroll") return "Payroll";
  if (entry.source === "Register") return "Register";
  // ef-045-5 — a crop sale posted by the Fields tab's own harvest form, same "own
  // literal label" treatment as Livestock/Payroll/Register above.
  if (entry.source === "Crop") return "Crop";
  if (entry.source !== "Receipt") return "Manual";
  return entry.accountId ? "Auto-categorized" : "Scanned";
}

export function EntryRow({ entry, vendor, account, isNew, anomaly }: EntryRowProps) {
  const vendorName = vendor?.name ?? "Unknown vendor";
  const categoryName = account?.name ?? "Uncategorized";
  // PascalCase: see api/types.ts header comment on enum serialization.
  const tone = account?.kind === "Income" ? "pos" : "neg";

  return (
    <li
      className={`entry-row${isNew ? " entry-row--new" : ""}`}
      // A screen reader announces new arrivals once, at the moment they land,
      // via the live region wrapping the whole list (see Dashboard.tsx) —
      // this element does not need its own aria-live to avoid double-announcing.
    >
      {/*
        Vendor initials, tinted by category. aria-hidden because it says nothing
        the row does not already say in words: the vendor name is immediately to
        its right and the category is on the line below. See lib/category.ts for
        why this replaced the per-category emoji.
      */}
      <span
        className={`entry-row__avatar entry-row__avatar--${toneForAccount(account)}`}
        aria-hidden="true"
      >
        {initialsForVendor(vendorName)}
      </span>
      <span className="entry-row__meta">
        <span className="entry-row__vendor">{vendorName}</span>
        <span className="entry-row__sub">
          {categoryName} · {sourceLabel(entry)} · {formatRelativeTime(entry.createdAt)}
        </span>
        {anomaly && (
          <PaymentAnomalyNotice
            variant="badge"
            attemptedLabel={anomaly.attempted.label}
            previouslyUsedLabels={anomaly.previouslyUsed.map((m) => m.label)}
          />
        )}
      </span>
      {/*
        ef-006 — the kept receipt.

        THE SLOT IS ALWAYS RENDERED; the LINK inside it is conditional. The first version
        rendered only the link, and only on rows that had one — which pushed that row's
        amount 44px left of every other row's. In a column of money, one figure out of
        alignment reads as a different KIND of number, and this list is scanned down the
        right edge. A fixed slot costs an empty span per row and keeps the column true.

        @ui-check: the LINK renders only when entry.receiptImageId is present. An entry
        with no photograph must show an empty slot, never a broken or empty affordance —
        offering a receipt that does not exist is worse than offering none, because a
        farmer would believe their documentation was filed.

        A plain link, not a lightbox. The image is served by a session-authenticated route
        on the same origin, so the browser sends the cookie and the OS renders the photo in
        whatever the farmer already uses. On a phone that is the native viewer, with pinch
        zoom for the small print — which is the entire reason to keep the receipt.
      */}
      <span className="entry-row__receipt-slot">
        {entry.receiptImageId && (
          <a
            className="entry-row__receipt"
            href={apiUrl(`/api/demo/receipts/${entry.receiptImageId}`)}
            target="_blank"
            rel="noreferrer"
            data-state="kept"
            // Named per row, because a list of twenty links all called "Receipt" is
            // unusable with a screen reader: the accessible name has to say WHICH.
            aria-label={`View the receipt kept for ${vendorName}`}
            title="View the kept receipt"
          >
            <Icon name="camera" size={14} />
            <span className="visually-hidden">Receipt kept</span>
          </a>
        )}
      </span>
      <span
        className={
          `entry-row__amount entry-row__amount--${tone}` +
          (Math.abs(entry.amount) >= NOTABLE_AMOUNT ? " entry-row__amount--notable" : "")
        }
      >
        {formatSignedMoney(entry.amount, account?.kind)}
      </span>
      {isNew && <span className="visually-hidden">New entry</span>}
    </li>
  );
}
